// @flow
import React, { Component } from 'react';

import keyBy from 'lodash-es/keyBy';
import styled, { type StyledComponent } from 'styled-components';
import { ButtonType } from '@rover/react-lib/components/buttons/Button';
import { DSToken } from '@rover/react-lib/styles';
import { EmergencyFormConstants } from '@rover/app/js/constants/translationConstants';
import RoverLoading from '@rover/react-lib/components/utils/RoverLoading';
import LinkButton from '@rover/react-lib/components/buttons/LinkButton/LinkButton';
import type { Pet } from '@rover/react-lib/types';
import { isMobileEmbedded } from '@rover/utilities/userAgent';

import type { AppProps } from '.';
import CareInstructions from './components/CareInstructions';
import ContactInfoContainer from './components/ContactInfo/ContactInfoContainer';
import VetInfo from './components/VetInfo';

type Props = {
  pets: Array<Pet>,
  getIsPetsLoading: boolean,
  savePet: (pet: Pet) => void,
  isVetComplete: boolean,
  isCareComplete: boolean,
  isContactComplete: boolean,
  personSummary: any,
  getIsPersonSummaryLoading: boolean,
  savePersonSummaryPrivate: ({ personData: any, personUrl: string }) => void,
};

type PageProps = AppProps & Props;

type State = {
  pets: {
    [key: string]: Pet,
  },
};
// Heading wrapper is used around an h3 tag over an actual Heading component
// to match sizing with titles in confirmation.mustache
const HeadingWrapper: StyledComponent<{}> = styled.div`
  text-align: center;
`;

const EmergencyFormWrapper: StyledComponent<{ showfaq: boolean }> = styled.div`
  padding-top: ${DSToken.SPACE_8X};
  padding-bottom: ${DSToken.SPACE_8X};
  margin-bottom: ${props => (props.showfaq ? DSToken.SPACE_8X : 0)};
  border-bottom: ${props => (props.showfaq ? `1px solid ${DSToken.BORDER_COLOR_PRIMARY}` : `none`)};
`;

const ButtonWrapper: StyledComponent<{}> = styled.div`
  text-align: center;
  margin-top: ${DSToken.SPACE_8X};
`;

const MutedTextWrapper: StyledComponent<{}> = styled.div`
  text-align: center;
  margin-bottom: ${DSToken.SPACE_5X};
`;

const MutedText: StyledComponent<{}> = styled.p`
  margin-bottom: ${DSToken.SPACE_4X};
  color: ${DSToken.TEXT_COLOR_SECONDARY};
`;

const EmergencyFormRoverLoading: StyledComponent<{}> = styled(RoverLoading)`
  padding-bottom: 80px;
`;

class EmergencyFormPage extends Component<PageProps, State> {
  state = {
    pets: keyBy(this.props.pets, pet => pet.url),
  };

  handleUpdate = (pet: Pet) => {
    this.setState(state => ({ pets: { ...state.pets, [pet.url]: pet } }));
  };

  handleSave = () => {
    const pets = Object.values(this.state.pets);

    // $FlowFixMe flow always returns Array<mixed> from Object.values ...
    pets.forEach(pet => this.props.savePet(pet));
  };

  render() {
    const {
      pets,
      getIsPetsLoading,
      isVetComplete,
      isCareComplete,
      isContactComplete,
      conversationUrl,
      showfaq,
      personSummary,
      getIsPersonSummaryLoading,
      savePersonSummaryPrivate,
    } = this.props;
    const petNames = pets.map(a => a.name);
    const petConjuction = petNames.join(', ');

    const loadingComplete = !getIsPetsLoading && !getIsPersonSummaryLoading;

    let title = EmergencyFormConstants.missedPetsSafetyInfo;

    if (isVetComplete && isCareComplete && isContactComplete) {
      title = EmergencyFormConstants.petsSafetyInfo;
    }

    const renderReturnButton = () => {
      let button = '';
      if (!isMobileEmbedded()) {
        button = (
          <ButtonWrapper>
            <LinkButton href={conversationUrl} buttonType={ButtonType.PRIMARY}>
              {EmergencyFormConstants.returnButton}
            </LinkButton>
          </ButtonWrapper>
        );
      }
      return button;
    };

    return loadingComplete ? (
      <EmergencyFormWrapper showfaq={showfaq}>
        <HeadingWrapper>
          <h3>{title}</h3>
        </HeadingWrapper>
        <MutedTextWrapper>
          <MutedText>{EmergencyFormConstants.filtersSitter(petConjuction)}</MutedText>
        </MutedTextWrapper>
        <VetInfo
          pets={Object.values(this.state.pets)}
          handleUpdate={this.handleUpdate}
          handleSave={this.handleSave}
          isVetComplete={isVetComplete}
        />
        <ContactInfoContainer
          personSummary={personSummary}
          savePersonSummaryPrivate={savePersonSummaryPrivate}
          isContactComplete={isContactComplete}
        />
        <CareInstructions
          pets={Object.values(this.state.pets)}
          handleUpdate={this.handleUpdate}
          handleSave={this.handleSave}
          isCareComplete={isCareComplete}
        />
        {renderReturnButton()}
      </EmergencyFormWrapper>
    ) : (
      <EmergencyFormRoverLoading isLoading={!loadingComplete} />
    );
  }
}

export default EmergencyFormPage;



// WEBPACK FOOTER //
// ../react-lib/src/pages/conversations/EmergencyFormPage/EmergencyFormPage.jsx